import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Spin } from '@arco-design/web-react'
import {
  IconBranch,
  IconCloud,
  IconDashboard,
  IconFire,
  IconGift,
  IconRobot,
  IconSettings,
  IconThunderbolt,
} from '@arco-design/web-react/icon'
import { useOptions } from '@/components/settings/useOptions'
import OperationSetting from '@/components/settings/OperationSetting'
import PaymentSetting from '@/components/settings/PaymentSetting'
import RatioSetting from '@/components/settings/RatioSetting'
import RateLimitSetting from '@/components/settings/RateLimitSetting'
import ModelSetting from '@/components/settings/ModelSetting'
import ModelDeploymentSetting from '@/components/settings/ModelDeploymentSetting'
import PerformanceSetting from '@/components/settings/PerformanceSetting'
import SystemSetting from '@/components/settings/SystemSetting'

const SETTING_TABS: Array<{
  key: string
  title: string
  icon: React.ReactNode
  Comp: React.ComponentType<{ options: ReturnType<typeof useOptions> }>
}> = [
  { key: 'operation', title: '运营', icon: <IconFire />, Comp: OperationSetting },
  { key: 'payment', title: '支付', icon: <IconGift />, Comp: PaymentSetting },
  { key: 'ratio', title: '分组/倍率', icon: <IconBranch />, Comp: RatioSetting },
  { key: 'ratelimit', title: '速率', icon: <IconThunderbolt />, Comp: RateLimitSetting },
  { key: 'models', title: '模型', icon: <IconRobot />, Comp: ModelSetting },
  { key: 'model-deployment', title: '部署', icon: <IconCloud />, Comp: ModelDeploymentSetting },
  { key: 'performance', title: '性能', icon: <IconDashboard />, Comp: PerformanceSetting },
  { key: 'system', title: '系统', icon: <IconSettings />, Comp: SystemSetting },
]

export default function SettingPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const tabFromQuery = searchParams.get('tab')
  const [activeTab, setActiveTab] = useState(() => {
    if (tabFromQuery && SETTING_TABS.some((t) => t.key === tabFromQuery)) return tabFromQuery
    return 'operation'
  })
  const options = useOptions()

  useEffect(() => {
    const t = searchParams.get('tab')
    if (t && SETTING_TABS.some((x) => x.key === t)) {
      setActiveTab(t)
    }
  }, [searchParams])

  const handleTabChange = (key: string) => {
    setActiveTab(key)
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('tab', key)
      return next
    })
  }

  if (options.loading) {
    return (
      <div className="settings-page">
        <div className="settings-loading">
          <Spin />
        </div>
      </div>
    )
  }

  const activeEntry = SETTING_TABS.find((t) => t.key === activeTab) ?? SETTING_TABS[0]
  const ActiveComp = activeEntry.Comp

  return (
    <div className="settings-page">
      <div className="settings-shell">
        <aside className="settings-sidebar" aria-label="系统设置导航">
          <div className="settings-sidebar-inner">
            {SETTING_TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={
                  activeTab === tab.key
                    ? 'settings-nav-item settings-nav-item-active'
                    : 'settings-nav-item'
                }
                aria-current={activeTab === tab.key ? 'page' : undefined}
                onClick={() => handleTabChange(tab.key)}
              >
                <span className="settings-nav-text">{tab.title}</span>
              </button>
            ))}
          </div>
        </aside>

        <section className="settings-content">
          <ActiveComp options={options} />
        </section>
      </div>
    </div>
  )
}
